import { tiendaDb } from '../lib/supabase';

// ─── TPV SERVICE ─────────────────────────────────────────────────────────────
// Todas las operaciones del TPV Físico contra el esquema tienda.

// ─── PRODUCTOS PARA TPV ───────────────────────────────────────────────────────
export const tpvProductosService = {
  /**
   * Carga todos los productos activos con su stock actual.
   * Filtra por búsqueda (nombre, EAN, PLU, código interno) y categoría.
   */
  getProductosConStock: async ({ search = '', categoriaId = null, familiaId = null } = {}) => {
    let query = tiendaDb
      ?.from('productos')
      ?.select(`
        id,
        codigo_interno,
        ean,
        plu,
        nombre,
        descripcion,
        pvp_venta,
        pvp_sin_iva,
        tipo_iva,
        venta_por_peso,
        imagen_url,
        stock_minimo,
        activo,
        categoria_id,
        familia_id,
        categoria:categorias(id, nombre),
        familia:familias_producto(id, nombre),
        inventario:inventario_actual(stock_actual, stock_disponible, stock_reservado)
      `)
      ?.eq('activo', true)
      ?.order('nombre');

    if (search?.trim()) {
      query = query?.or(
        `nombre.ilike.%${search}%,codigo_interno.ilike.%${search}%,ean.ilike.%${search}%,plu.ilike.%${search}%`
      );
    }
    if (categoriaId) query = query?.eq('categoria_id', categoriaId);
    if (familiaId) query = query?.eq('familia_id', familiaId);

    const { data, error } = await query;
    if (error) throw error;

    // Normalizar: extraer stock del array inventario
    return (data || [])?.map(p => ({
      ...p,
      stock_disponible: p?.inventario?.[0]?.stock_disponible ?? 0,
      stock_actual: p?.inventario?.[0]?.stock_actual ?? 0,
    }));
  },

  /**
   * Busca un producto por EAN, PLU o código interno (para Epelsa / escáner).
   */
  buscarPorCodigo: async (codigo) => {
    if (!codigo?.trim()) return null;
    const { data, error } = await tiendaDb
      ?.from('productos')
      ?.select(`
        id, codigo_interno, ean, plu, nombre, pvp_venta, pvp_sin_iva,
        tipo_iva, venta_por_peso, imagen_url, activo,
        inventario:inventario_actual(stock_actual, stock_disponible)
      `)
      ?.or(`ean.eq.${codigo},plu.eq.${codigo},codigo_interno.eq.${codigo}`)
      ?.eq('activo', true)
      ?.limit(1)
      ?.single();

    if (error && error?.code !== 'PGRST116') throw error;
    if (!data) return null;
    return {
      ...data,
      stock_disponible: data?.inventario?.[0]?.stock_disponible ?? 0,
      stock_actual: data?.inventario?.[0]?.stock_actual ?? 0,
    };
  },

  /**
   * Obtiene las familias activas para el filtro de categorías del TPV.
   */
  getFamilias: async () => {
    const { data, error } = await tiendaDb
      ?.from('familias_producto')
      ?.select('id, nombre, orden')
      ?.eq('activo', true)
      ?.order('orden');
    if (error) throw error;
    return data || [];
  },
};

// ─── PROCESADO DE VENTA ───────────────────────────────────────────────────────
export const tpvVentaService = {
  /**
   * Genera el número de venta siguiente.
   * Formato: TPV-YYYYMMDD-NNNN
   */
  generarNumeroVenta: async () => {
    const hoy = new Date();
    const fecha = hoy?.toISOString()?.slice(0, 10)?.replace(/-/g, '');
    const prefijo = `TPV-${fecha}-`;

    const { data } = await tiendaDb
      ?.from('ventas')
      ?.select('numero_venta')
      ?.ilike('numero_venta', `${prefijo}%`)
      ?.order('numero_venta', { ascending: false })
      ?.limit(1);

    const ultimo = data?.[0]?.numero_venta;
    const seq = ultimo ? parseInt(ultimo?.split('-')?.[3] || '0', 10) + 1 : 1;
    return `${prefijo}${String(seq)?.padStart(4, '0')}`;
  },

  /**
   * Procesa el cobro completo en una única transacción lógica:
   * 1. Crea venta en tienda.ventas
   * 2. Crea líneas en tienda.lineas_venta
   * 3. Descuenta inventario en tienda.inventario_actual
   * 4. Crea movimientos de stock en tienda.movimientos_stock
   * 5. Actualiza tienda.caja_diaria
   *
   * @param {Object} params
   * @param {Array}  params.lineas        - Líneas del carrito
   * @param {string} params.formaPago     - EFECTIVO | TARJETA | BIZUM | TRANSFERENCIA
   * @param {number} params.baseImponible
   * @param {number} params.iva
   * @param {number} params.total
   * @param {string} [params.origen]      - null | 'EPELSA'
   * @param {string} [params.observaciones]
   */
  procesarVenta: async ({ lineas, formaPago, baseImponible, iva, total, origen = null, observaciones = null }) => {
    // ── 1. Validar stock antes de proceder ────────────────────────────────
    for (const linea of lineas) {
      if (!linea?.ventaPorPeso) {
        const { data: inv } = await tiendaDb
          ?.from('inventario_actual')
          ?.select('stock_disponible')
          ?.eq('producto_id', linea?.productoId)
          ?.single();
        const stockDisp = inv?.stock_disponible ?? 0;
        if (stockDisp < linea?.cantidad) {
          throw new Error(`Stock insuficiente para "${linea?.nombre}". Disponible: ${stockDisp}, solicitado: ${linea?.cantidad}`);
        }
      }
    }

    // ── 2. Crear venta ────────────────────────────────────────────────────
    const numeroVenta = await tpvVentaService?.generarNumeroVenta();
    const { data: venta, error: errVenta } = await tiendaDb
      ?.from('ventas')
      ?.insert({
        numero_venta: numeroVenta,
        fecha: new Date()?.toISOString(),
        canal: 'TIENDA',
        origen: origen,
        base_imponible: parseFloat(baseImponible?.toFixed(2)),
        iva: parseFloat(iva?.toFixed(2)),
        total: parseFloat(total?.toFixed(2)),
        forma_pago: formaPago,
        estado: 'COBRADO',
        observaciones: observaciones,
      })
      ?.select()
      ?.single();

    if (errVenta) throw new Error(`Error al crear venta: ${errVenta?.message}`);

    // ── 3. Crear líneas de venta ──────────────────────────────────────────
    const lineasInsert = lineas?.map(l => ({
      venta_id: venta?.id,
      producto_id: l?.productoId,
      cantidad: l?.cantidad,
      precio_unitario: l?.precioUnitario,
      descuento: l?.descuento ?? 0,
      subtotal: parseFloat((l?.cantidad * l?.precioUnitario)?.toFixed(2)),
    }));

    const { error: errLineas } = await tiendaDb
      ?.from('lineas_venta')
      ?.insert(lineasInsert);

    if (errLineas) throw new Error(`Error al crear líneas: ${errLineas?.message}`);

    // ── 4. Descontar inventario + crear movimientos de stock ──────────────
    for (const linea of lineas) {
      // Descontar stock: leer → calcular → escribir
      const { data: invActual } = await tiendaDb
        ?.from('inventario_actual')
        ?.select('stock_actual')
        ?.eq('producto_id', linea?.productoId)
        ?.single();

      if (invActual) {
        const nuevoStock = Math.max(0, (invActual?.stock_actual ?? 0) - linea?.cantidad);
        await tiendaDb
          ?.from('inventario_actual')
          ?.update({
            stock_actual: nuevoStock,
            ultima_actualizacion: new Date()?.toISOString(),
          })
          ?.eq('producto_id', linea?.productoId);
      }

      // Crear movimiento de stock
      await tiendaDb
        ?.from('movimientos_stock')
        ?.insert({
          producto_id: linea?.productoId,
          tipo_movimiento: 'VENTA',
          cantidad: -Math.abs(linea?.cantidad),
          observaciones: `Venta TPV ${numeroVenta}`,
          referencia_tipo: 'VENTA',
          referencia_id: venta?.id,
        });
    }

    // ── 5. Actualizar caja diaria ─────────────────────────────────────────
    const hoy = new Date()?.toISOString()?.slice(0, 10);
    const { data: cajaExistente } = await tiendaDb
      ?.from('caja_diaria')
      ?.select('id, efectivo, tarjeta, bizum, transferencia')
      ?.eq('fecha', hoy)
      ?.single();

    const importeTotal = parseFloat(total?.toFixed(2));
    const campoForma = {
      EFECTIVO: 'efectivo',
      TARJETA: 'tarjeta',
      BIZUM: 'bizum',
      TRANSFERENCIA: 'transferencia',
    }?.[formaPago] ?? 'efectivo';

    if (cajaExistente) {
      const nuevoValor = (cajaExistente?.[campoForma] ?? 0) + importeTotal;
      await tiendaDb
        ?.from('caja_diaria')
        ?.update({ [campoForma]: nuevoValor })
        ?.eq('id', cajaExistente?.id);
    } else {
      await tiendaDb
        ?.from('caja_diaria')
        ?.insert({
          fecha: hoy,
          [campoForma]: importeTotal,
        });
    }

    return venta;
  },
};
